import React, { useState } from 'react';
import { IProvisionOneSignalAppRes } from '../../../../../../networked-shared-interfaces/platform-admin/onesignal-route';
import { useProvisionOneSignalApp } from '../../hooks/useProvisionOneSignalApp';

interface IOneSignalProvisionCardProps {
	communityId: string;
	/** True when the API reports oneSignalStatus as Active/completed. */
	isCompleted: boolean;
	/** Called after a successful provision to refetch the community and update the stepper. */
	onRefetch: () => void;
}

const OneSignalProvisionCard: React.FC<IOneSignalProvisionCardProps> = ({ communityId, isCompleted, onRefetch }) => {
	const { loading, error, result, provision } = useProvisionOneSignalApp();
	const [copied, setCopied] = useState(false);

	const canProvision = !isCompleted && !result;

	const handleProvision = async () => {
		const res: IProvisionOneSignalAppRes | null = await provision(communityId);
		if (res) {
			onRefetch();
		}
	};

	const handleCopy = () => {
		if (!result?.appId) return;
		navigator.clipboard.writeText(result.appId).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		});
	};

	return (
		<div className="np-os-card card border-0 shadow-sm mt-3">
			<div className="card-body p-4">
				<div className="d-flex align-items-center mb-3">
					<div className="np-os-icon-wrap mr-3">
						<i className="fas fa-bell fa-lg text-primary"></i>
					</div>
					<div>
						<h6 className="mb-0 font-weight-bold">Provision OneSignal App</h6>
						<small className="text-muted">
							Creates a new OneSignal application for this community and stores the credentials on the platform.
						</small>
					</div>
				</div>

				{isCompleted && !result && (
					<div className="alert alert-success py-2 mb-0">
						<i className="fas fa-check-circle mr-2"></i>
						<strong>OneSignal is active for this community.</strong>
					</div>
				)}

				{result && (
					<div className="np-os-success alert alert-success py-3 mb-0">
						<div className="d-flex align-items-center mb-2">
							<i className="fas fa-check-circle mr-2"></i>
							<strong>OneSignal app provisioned successfully.</strong>
						</div>
						<label className="small text-muted mb-1 d-block">App ID</label>
						<div className="input-group input-group-sm">
							<input
								type="text"
								className="form-control np-os-appid-input"
								readOnly
								value={result.appId}
							/>
							<div className="input-group-append">
								<button
									className="btn btn-outline-secondary"
									type="button"
									onClick={handleCopy}
								>
									<i className={`fas ${copied ? 'fa-check text-success' : 'fa-copy'} mr-1`}></i>
									{copied ? 'Copied' : 'Copy'}
								</button>
							</div>
						</div>
						{result.status !== undefined && (
							<small className="text-muted mt-1 d-block">
								Status code: <code>{result.status}</code>
							</small>
						)}
					</div>
				)}

				{error && (
					<div className="alert alert-danger py-2 mb-3">
						<i className="fas fa-exclamation-circle mr-1"></i>
						{error}
					</div>
				)}

				{canProvision && (
					<button
						type="button"
						className="np-os-btn btn btn-primary btn-lg px-5 mt-3"
						onClick={handleProvision}
						disabled={loading}
					>
						{loading ? (
							<>
								<span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
								Provisioning…
							</>
						) : (
							<>
								<i className="fas fa-rocket mr-2"></i>
								Provision OneSignal App
							</>
						)}
					</button>
				)}
			</div>
		</div>
	);
};

export default OneSignalProvisionCard;
